import React, { useState } from 'react';
import { useMutation, useApolloClient, gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CREATE_VOTE, UPDATE_VOTE, GET_VOTING_ARTICLES, GET_REPORTED_ARTICLES, TOGGLE_VOTING_RECOMMENDATION } from '../graphql/voting';
import AddToLibraryModal from './AddToLibraryModal';
import VotingEditModal from './VotingEditModal';
import ReportModal from './ReportModal';

const VotingCardMobile = ({ article, onVoteSuccess, onViewDetail, viewMode = 'all', currentUserId = null }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [isVoting, setIsVoting] = useState(false);
  const [userVote, setUserVote] = useState(article.userVote);
  const [isAddToLibraryModalOpen, setIsAddToLibraryModalOpen] = useState(false);
  const [showSecretInfo, setShowSecretInfo] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false); // 控制是否展開
  
  const client = useApolloClient();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  // 檢查是否應該顯示編輯按鈕
  // 1. 我的投票文模式下，創建者可以編輯
  // 2. 半管理員可以編輯所有文章（包括被檢舉貼文列表）
  const isCreator = currentUserId && article.creatorId === currentUserId;
  const isSemiAdmin = currentUser?.isSemiAdmin || false;
  const shouldShowEditButton = (viewMode === 'myPosts' && isCreator) || 
                                (viewMode === 'all' && isSemiAdmin) || 
                                (viewMode === 'reported' && isSemiAdmin);
  
  // 檢查是否應該顯示檢舉按鈕
  // 一般使用者可以檢舉（在投票廣場模式下，且不是自己的文章，且不是半管理員）
  const shouldShowReportButton = viewMode === 'all' && 
                                  currentUser && 
                                  !isSemiAdmin && 
                                  !shouldShowEditButton &&
                                  article.status !== 'REPORTED';

  const [createVote] = useMutation(CREATE_VOTE, {
    update: (cache, { data }) => {
      if (!data?.createVote?.success) return;

      try {
        const articleId = data.createVote.vote.articleId;
        const selectedOption = data.createVote.vote.selectedOption;

        // 讀取當前文章的快取數據
        const articleRef = cache.readFragment({
          id: cache.identify({ __typename: 'VotingArticle', id: articleId }),
          fragment: gql`
            fragment CurrentArticle on VotingArticle {
              voteCount
              voteResults {
                optionIndex
                count
                percentage
              }
            }
          `
        });

        if (articleRef) {
          const currentVoteCount = articleRef.voteCount || 0;
          const newVoteCount = currentVoteCount + 1;
          const currentVoteResults = articleRef.voteResults || [];

          // 更新投票結果
          const newVoteResults = currentVoteResults.map((result, index) => {
            if (result.optionIndex === selectedOption) {
              // 更新選中的選項
              const newCount = (result.count || 0) + 1;
              return {
                ...result,
                count: newCount,
                percentage: newVoteCount > 0 ? Math.round((newCount / newVoteCount) * 100) : 0
              };
            }
            // 重新計算其他選項的百分比
            return {
              ...result,
              percentage: newVoteCount > 0 ? Math.round(((result.count || 0) / newVoteCount) * 100) : 0
            };
          });

          // 使用 cache.modify 更新快取
          cache.modify({
            id: cache.identify({ __typename: 'VotingArticle', id: articleId }),
            fields: {
              voteCount() {
                return newVoteCount;
              },
              voteResults() {
                return newVoteResults;
              },
              userVote() {
                return data.createVote.vote;
              }
            }
          });

          console.log('投票快取已精準更新');
        }
      } catch (error) {
        console.error('投票快取更新失敗:', error);
        // 如果精準更新失敗，使用 evict 作為備用方案
        client.cache.evict({ fieldName: 'votingArticles' });
        client.cache.gc();
      }
    },
    onCompleted: (data) => {
      if (data.createVote.success) {
        setUserVote(data.createVote.vote);
        setIsVoting(false);
        if (onVoteSuccess) {
          onVoteSuccess(data.createVote.vote);
        }
      }
    },
    onError: (error) => {
      console.error('投票失敗:', error);
      setIsVoting(false);
    }
  });

  const [updateVote] = useMutation(UPDATE_VOTE, {
    update: (cache, { data }) => {
      if (!data?.updateVote?.success) return;

      try {
        const articleId = data.updateVote.vote.articleId;
        const newSelectedOption = data.updateVote.vote.selectedOption;

        // 讀取當前使用者投票狀態來確定舊的選項
        const currentUserVote = cache.readFragment({
          id: cache.identify({ __typename: 'VotingArticle', id: articleId }),
          fragment: gql`
            fragment CurrentUserVote on VotingArticle {
              userVote {
                selectedOption
              }
              voteResults {
                optionIndex
                count
                percentage
              }
              voteCount
            }
          `
        });

        if (currentUserVote) {
          const oldSelectedOption = currentUserVote.userVote?.selectedOption;
          const currentVoteResults = [...(currentUserVote.voteResults || [])];

          // 如果使用者改變了投票選項，更新統計
          if (oldSelectedOption !== undefined && oldSelectedOption !== newSelectedOption) {
            // 減少舊選項的投票數
            if (currentVoteResults[oldSelectedOption]) {
              currentVoteResults[oldSelectedOption] = {
                ...currentVoteResults[oldSelectedOption],
                count: Math.max(0, (currentVoteResults[oldSelectedOption].count || 0) - 1)
              };
            }

            // 增加新選項的投票數
            if (currentVoteResults[newSelectedOption]) {
              currentVoteResults[newSelectedOption] = {
                ...currentVoteResults[newSelectedOption],
                count: (currentVoteResults[newSelectedOption].count || 0) + 1
              };
            }

            // 重新計算百分比（總投票數不變）
            // 創建新的對象，而不是修改只讀對象
            const totalVotes = currentUserVote.voteCount || 0;
            const updatedVoteResults = currentVoteResults.map(result => {
              if (result) {
                return {
                  ...result,
                  percentage: totalVotes > 0 ? Math.round((result.count / totalVotes) * 100) : 0
                };
              }
              return result;
            });

            // 使用 cache.modify 更新快取
            cache.modify({
              id: cache.identify({ __typename: 'VotingArticle', id: articleId }),
              fields: {
                voteResults() {
                  return updatedVoteResults;
                },
                userVote() {
                  return data.updateVote.vote;
                }
              }
            });
          } else {
            // 如果只是更新同一選項或初次投票，只更新使用者投票狀態
            cache.modify({
              id: cache.identify({ __typename: 'VotingArticle', id: articleId }),
              fields: {
                userVote() {
                  return data.updateVote.vote;
                }
              }
            });
          }
        }

        console.log('更新投票快取已精準更新');
      } catch (error) {
        console.error('更新投票快取失敗:', error);
      }
    },
    onCompleted: (data) => {
      if (data.updateVote.success) {
        setUserVote(data.updateVote.vote);
        setIsVoting(false);
        if (onVoteSuccess) {
          onVoteSuccess(data.updateVote.vote);
        }
      }
    },
    onError: (error) => {
      console.error('更新投票失敗:', error);
      setIsVoting(false);
    }
  });

  const [toggleRecommendation] = useMutation(TOGGLE_VOTING_RECOMMENDATION, {
    update: (cache, { data }) => {
      if (!data?.toggleVotingRecommendation?.success) return;

      try {
        const articleId = data.toggleVotingRecommendation.article.id;
        const isRecommended = data.toggleVotingRecommendation.article.isRecommended;

        // 更新具體文章的推薦狀態
        cache.modify({
          id: cache.identify({ __typename: 'VotingArticle', id: articleId }),
          fields: {
            isRecommended() {
              return isRecommended;
            }
          }
        });

        console.log('推薦狀態快取已精準更新');
      } catch (error) {
        console.error('推薦狀態快取更新失敗:', error);
      }
    },
    onCompleted: (data) => {
      if (data.toggleVotingRecommendation.success) {
        // 快取已在 update 函數中更新，不需要額外操作
      }
    },
    onError: (error) => {
      console.error('切換推薦狀態失敗:', error);
      alert('切換推薦狀態失敗：' + error.message);
    }
  });

  const truncateText = (text, maxLength = 30) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const handleToggleRecommendation = async (e) => {
    e.stopPropagation();
    try {
      await toggleRecommendation({
        variables: {
          articleId: article.id
        }
      });
    } catch (error) {
      console.error('切換推薦狀態失敗:', error);
    }
  };

  const handleVote = async (optionIndex) => {
    // 如果正在投票，或者點擊的是已經投過的選項，則不執行
    if (isVoting || (userVote && userVote.selectedOption === optionIndex)) return;
    
    setIsVoting(true);
    setSelectedOption(optionIndex);
    
    try {
      // 如果已經投票過，使用 updateVote；否則使用 createVote
      if (userVote) {
        await updateVote({
          variables: {
            input: {
              articleId: article.id,
              selectedOption: optionIndex
            }
          }
        });
      } else {
        await createVote({
          variables: {
            input: {
              articleId: article.id,
              selectedOption: optionIndex
            }
          }
        });
      }
    } catch (error) {
      console.error('投票錯誤:', error);
      setIsVoting(false);
      setSelectedOption(null);
    }
  };

  const handleAddToLibraryClick = () => {
    setIsAddToLibraryModalOpen(true);
  };

  const handleAddToLibrarySuccess = () => {
    client.cache.evict({ fieldName: 'votingArticles' });
    client.cache.gc();
  };

  const handleViewDetail = () => {
    if (onViewDetail) {
      onViewDetail(article.id);
    } else {
      navigate(`/voting/${article.id}`);
    }
  };

  const calculatePercentage = (count, total) => {
    if (total === 0) return '0.00';
    return ((count / total) * 100).toFixed(2);
  };

  const totalVotes = article.voteResults?.reduce((sum, result) => sum + result.count, 0) || 0;
  
  // 選項顯示邏輯：超過6個選項時只顯示前6個
  const totalOptions = article.question?.options?.length || 0;
  const shouldTruncateOptions = totalOptions > 6;
  const displayOptions = shouldTruncateOptions 
    ? article.question.options.slice(0, 6) 
    : article.question?.options || [];

  // 截取描述文字爲兩行，但保留票數顯示
  const truncateDescription = (text, maxLength = 80) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // 處理選項顯示：先保留前6個，然後逐個縮短直到符合版面
  const getCompactOptions = (options) => {
    if (!options || options.length === 0) return [];
    
    // Step 1: 保留前6個選項
    const displayOptions = options.slice(0, 6);
    
    // Step 2: 移動端每個選項的最大長度更短（約12-15個字符）
    const maxLengthPerOption = 14;
    
    return displayOptions.map(option => {
      const text = option.text || '';
      if (text.length <= maxLengthPerOption) {
        return text;
      }
      // 如果超過長度，截斷並添加省略號
      return text.substring(0, maxLengthPerOption - 2) + '..';
    });
  };

  // 精簡模式渲染
  if (!isExpanded) {
    const compactOptions = getCompactOptions(article.question?.options);
    const description = article.question?.description || '';
    
    return (
      <div 
        onClick={() => setIsExpanded(true)}
        className={`relative bg-white rounded-lg shadow-sm cursor-pointer overflow-hidden ${
          article.question?.secret 
            ? 'border-2 border-yellow-400 shadow-yellow-100' 
            : 'border border-gray-200'
        }`}
      >
        {/* 標題區域 */}
        <div className="p-3 relative z-10">
          {/* 標題 */}
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-base font-semibold text-gray-900 hover:text-purple-600 transition-colors flex-1 break-words">
              {article.question?.title || '未知問題'}
            </h3>
            {article.isRecommended && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-800 border border-orange-300 whitespace-nowrap flex-shrink-0">
                ⭐
              </span>
            )}
            {article.status === 'REPORTED' && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-300 whitespace-nowrap flex-shrink-0">
                ⚠️
              </span>
            )}
            {article.reportCount > 0 && viewMode === 'reported' && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-300 whitespace-nowrap flex-shrink-0">
                🚨 {article.reportCount}
              </span>
            )}
            {article.question?.secret && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300 whitespace-nowrap flex-shrink-0">
                🔒 祕密
              </span>
            )}
          </div>
          
          {/* 兩行內文 + 投票數（內文省略後依舊顯示票數） */}
          <div className="mb-2">
            <p className="text-sm text-gray-600 line-clamp-2 break-words">
              {truncateDescription(description)}
            </p>
            <span className="text-sm text-gray-600">({totalVotes}票)</span>
          </div>
          
          {/* 選項壓縮顯示一行 */}
          <div className="flex flex-wrap gap-1.5 text-xs text-gray-500">
            {compactOptions.map((optionText, index) => (
              <span key={index} className="px-2 py-0.5 bg-gray-50 rounded border border-gray-200 whitespace-nowrap">
                {optionText}
              </span>
            ))}
          </div>
        </div>

        {/* 漸層覆蓋層（淺黃色，下方較濃，但更輕微） */}
        <div className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none z-20 bg-gradient-to-t from-yellow-50/60 via-yellow-50/30 to-transparent"></div>
        
        {/* 下拉提示標記 */}
        <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 z-30 pointer-events-none">
          <div className="flex flex-col items-center">
            <svg 
              className="w-5 h-5 text-gray-400" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
              style={{
                animation: 'bounce-every-5s 5s ease-in-out infinite'
              }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            <span className={`text-xs mt-0.5 ${userVote ? 'text-yellow-600' : 'text-purple-600'}`}>{userVote ? '展開' : '投票'}</span>
          </div>
        </div>
        <style>{`
          @keyframes bounce-every-5s {
            0%, 96%, 100% {
              transform: translateY(0);
            }
            2% {
              transform: translateY(-8px);
            }
            4% {
              transform: translateY(0);
            }
          }
        `}</style>

        <AddToLibraryModal
          isOpen={isAddToLibraryModalOpen}
          onClose={() => setIsAddToLibraryModalOpen(false)}
          onSuccess={handleAddToLibrarySuccess}
          question={article.question}
        />
      </div>
    );
  }

  // 展開模式（現有樣式）
  return (
    <div className={`bg-white rounded-lg shadow-sm ${
      article.question?.secret 
        ? 'border-2 border-yellow-400 shadow-yellow-100' 
        : 'border border-gray-200'
    }`}>
      {/* 祕密問題提示條 - 只在點擊後顯示 */}
      {article.question?.secret && showSecretInfo && (
        <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-b border-yellow-200 px-3 py-2">
          <div className="flex items-center justify-between text-xs text-yellow-800">
            <div className="flex items-center flex-1">
              <span className="mr-1">🔒</span>
              <span className="font-medium">祕密問題：不顯示在配對詳情和共同話題，僅參與分數計算</span>
            </div>
            <button 
              onClick={() => setShowSecretInfo(false)}
              className="text-yellow-600 hover:text-yellow-800 ml-2 flex-shrink-0"
            >
              ✕
            </button>
          </div>
        </div>
      )}
      
      {/* 標題區域 */}
      <div className="p-3 border-b border-gray-100">
        <div className="flex items-center gap-2 mb-1">
          <h3 
            onClick={handleViewDetail}
            className="text-base font-semibold text-gray-900 cursor-pointer hover:text-purple-600 transition-colors flex-1 break-words"
          >
            {article.question?.title || '未知問題'}
          </h3>
          {article.isRecommended && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-800 border border-orange-300 whitespace-nowrap flex-shrink-0">
              ⭐
            </span>
          )}
          {article.question?.secret && (
            <button
              onClick={() => setShowSecretInfo(!showSecretInfo)}
              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300 hover:bg-yellow-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              🔒 祕密
            </button>
          )}
        </div>
        <p 
          onClick={handleViewDetail}
          className="text-sm text-gray-600 mb-2 cursor-pointer hover:text-gray-800 transition-colors line-clamp-3 break-words whitespace-pre-wrap"
        >
          {article.question?.description || ''}
        </p>
        <div className="flex items-center text-xs text-gray-500">
          <div className="flex items-center space-x-2">
            {/* 如果是匿名文章，但當前使用者是半管理員，顯示創建者信息 */}
            {article.isAnonymous && !isSemiAdmin ? (
              <span className="flex items-center">
                <span className="mr-1">🎭</span>
                <span>匿名使用者</span>
              </span>
            ) : article.creator?.id ? (
              <span
                onClick={() => navigate(`/user/${article.creator.id}`)}
                className="cursor-pointer hover:text-blue-600 hover:underline transition-colors"
              >
                👤 {article.creator.username || '匿名'}
                {article.isAnonymous && isSemiAdmin && (
                  <span className="ml-1 text-yellow-600" title="匿名文章（半管理員可見）">🎭</span>
                )}
              </span>
            ) : (
              <span>👤 {article.creator?.username || '匿名'}</span>
            )}
          </div>
        </div>
      </div>

      {/* 問題和選項區域 */}
      <div className="p-3">
        {article.question && (
          <div>
            {/* 選項列表 - 移動端優化 */}
            <div className="space-y-2">
              {displayOptions.map((option, index) => {
                const voteResult = article.voteResults?.find(r => r.optionIndex === index);
                const voteCount = voteResult?.count || 0;
                const percentage = calculatePercentage(voteCount, totalVotes);
                const isUserVoted = userVote?.selectedOption === index;
                const isSelected = selectedOption === index;
                
                return (
                  <div key={index} className="relative overflow-hidden">
                    <button
                      onClick={() => handleVote(index)}
                      disabled={isVoting || (userVote && userVote.selectedOption === index)}
                      className={`w-full p-3 text-left rounded-lg border transition-all duration-200 relative ${
                        isUserVoted
                          ? 'border-yellow-400 border-2'
                          : isSelected
                          ? 'border-purple-400'
                          : 'border-gray-200 hover:border-gray-300'
                      } ${isVoting ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                    >
                      {/* 進度條背景 - 只在 showVoteResults 為 true 時顯示 */}
                      {article.showVoteResults !== false && totalVotes > 0 && (
                        <div 
                          className={`absolute inset-0 transition-all duration-300 ${
                            isUserVoted ? 'bg-yellow-100' : 'bg-gray-100'
                          }`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      )}
                      
                      <div className="flex items-center justify-between relative z-10">
                        <span className="font-medium text-sm flex-1 mr-2 truncate">{option.text}</span>
                        <div className="flex items-center space-x-2 flex-shrink-0">
                          {userVote && isUserVoted && (
                            <span className="text-blue-600 text-xs">✓ 已投票</span>
                          )}
                          {/* 根據 showVoteResults 標誌顯示/隱藏票數分佈 */}
                          {article.showVoteResults !== false && (
                            <span className="text-xs text-gray-700 font-medium">
                              {voteCount} 票 {percentage}%
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                    
                    {isVoting && isSelected && (
                      <div className="absolute inset-0 flex items-center justify-center bg-purple-50 rounded-lg">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-400"></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* 如果選項被截斷，顯示提示 */}
            {shouldTruncateOptions && (
              <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded text-center">
                <button 
                  onClick={handleViewDetail}
                  className="text-xs text-blue-700 hover:text-blue-900 font-medium"
                >
                  共 {totalOptions} 個選項 →
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 底部欄位：收起 | 票數 | 加入擇友標準 */}
      <div className="px-3 py-2 bg-gray-50 rounded-b-lg">
        <div className="flex items-center justify-between text-xs gap-2">
          <button
            onClick={() => setIsExpanded(false)}
            className="px-2 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all text-xs font-medium whitespace-nowrap"
          >
            收起
          </button>
          <div className="flex items-center space-x-1.5 flex-shrink-0">
            <span className="text-gray-300">|</span>
            <span className="text-gray-600 whitespace-nowrap">🗳️ {totalVotes}</span>
            <span className="text-gray-300">|</span>
            {shouldShowEditButton && (
              <>
                <button 
                  onClick={() => setIsEditModalOpen(true)}
                  className="px-2 py-1 rounded bg-yellow-100 text-yellow-700 hover:bg-yellow-200 transition-all text-xs font-medium whitespace-nowrap"
                >
                  編輯
                </button>
                <span className="text-gray-300">|</span>
              </>
            )}
            {shouldShowReportButton && (
              <>
                <button 
                  onClick={() => setIsReportModalOpen(true)}
                  className="px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 transition-all text-xs font-medium whitespace-nowrap"
                >
                  檢舉
                </button>
                <span className="text-gray-300">|</span>
              </>
            )}
            {/* 半管理者推薦按鈕 */}
            {isSemiAdmin && (
              <>
                <button 
                  onClick={handleToggleRecommendation}
                  className={`px-2 py-1 rounded transition-all text-xs font-medium whitespace-nowrap ${
                    article.isRecommended
                      ? 'bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-700 hover:from-yellow-200 hover:to-orange-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {article.isRecommended ? '⭐' : '☆'}
                </button>
                <span className="text-gray-300">|</span>
              </>
            )}
            {/* 我的投票文模式下不顯示加入擇友標準按鈕 */}
            {viewMode !== 'myPosts' && (
              <>
                <span className="text-gray-300">|</span>
                <button 
                  onClick={handleAddToLibraryClick}
                  className="px-2 py-1 rounded bg-gradient-to-r from-pink-400 to-purple-500 text-white hover:from-pink-500 hover:to-purple-600 transition-all text-xs font-medium whitespace-nowrap"
                >
                  {article.isInQuestionSet ? '修改擇友條件' : '加入擇友標準'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <AddToLibraryModal
        isOpen={isAddToLibraryModalOpen}
        onClose={() => setIsAddToLibraryModalOpen(false)}
        onSuccess={handleAddToLibrarySuccess}
        question={article.question}
      />

      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        articleId={article.id}
        articleTitle={article.question?.title}
      />

      <VotingEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        article={article}
        onSuccess={() => {
          // 刷新數據
          client.cache.evict({ fieldName: 'votingArticles' });
          client.cache.evict({ fieldName: 'reportedArticles' });
          client.cache.gc();
          client.refetchQueries({
            include: [GET_VOTING_ARTICLES, GET_REPORTED_ARTICLES]
          });
        }}
      />
    </div>
  );
};

export default VotingCardMobile; 